import Fragment from "./fragment";
import FragmentDataType from "../enums/fragment-data-type";
import FragmentSummary from "./fragment-summary";
import { playerReducer } from "../../../storage/reducers";
import FootballState from "../state/football-state";
import { FragmentMessages } from "../../common/fragment-messages";
import ReturnedByPositionPref from "./position-pref/returned-by-position-pref";
import ReturnedByDefaultValue from "./default-value/returned-by-default-value";

export default class Returned extends Fragment {
    constructor(returnedBy, returnedAt) {
        super();
        this.code = "R";
        this.returnedBy = returnedBy;
        this.returnedAt = returnedAt;

        this.requiredData = [
            {
                name: "returnedBy",
                type: FragmentDataType.Player,
                appliesToOpponent: () => true,
                displayName: "Returned By",
                title: (team) => `Returned By: ${team.teamName}`,
                subtitle: (team) =>
                    `Select a player
                     from ${team.teamName} or enter the roster number`,
                isEditable: true,
                positionPref: (play) =>
                    new ReturnedByPositionPref(play, this).resolve(),
                defaultValue: ({ play, summaryContext }) =>
                    new ReturnedByDefaultValue(
                        play,
                        this,
                        summaryContext
                    ).resolve()
            },
            {
                name: "returnedAt",
                type: FragmentDataType.FieldPosition,
                displayName: "At",
                title: "At",
                subtitle: FragmentMessages.FB_TEAM_FIELD_POSITION,
                requiredAfterNextState: [
                    FootballState.Tackle,
                    FootballState.OutOfBounds
                ],
                showEndZoneYards: (gameConfig) =>
                    gameConfig.general.includeEndZoneYardage
            }
        ];
    }

    getFragmentTeamId() {
        return this.returnedBy?.teamId;
    }

    changesPossession() {
        return true;
    }

    getFieldPosition() {
        return this.returnedAt;
    }

    getPlayers() {
        return [this.returnedBy];
    }

    summarize(summaryContext, stringifier, play) {
        const isOnSideKickPlay =
            play.getPreceedingFragment(this, FootballState.OnSide)
                ?.associatedState === FootballState.OnSide;
        const goalPosition = summaryContext.fieldPosition;

        const messages = [
            `to the ${stringifier.niceFieldPosition(
                summaryContext.gameConfig.general.fieldLength,
                goalPosition,
                summaryContext.homeTeam?.abbrName,
                summaryContext.awayTeam?.abbrName
            )},`
        ];
        if (isOnSideKickPlay) {
            messages.push(
                `returned by ${stringifier.firstInitial(this.returnedBy)} for`
            );
        } else {
            messages.push(
                `${stringifier.firstInitial(this.returnedBy)} returned`
            );
        }

        messages.push(
            `${stringifier.gain(
                Object.assign(summaryContext, {
                    homeTeamHasPossession:
                        this?.returnedBy?.teamId === summaryContext.homeTeamId
                }),
                goalPosition,
                this.returnedAt
            )}`
        );

        return new FragmentSummary(messages.join(" "));
    }

    updateSummaryContext(summaryContext) {
        summaryContext.fieldPosition = this.returnedAt;
        summaryContext.playerInPossession = this.returnedBy;
        summaryContext.homeTeamHasPossession =
            this?.returnedBy?.teamId === summaryContext.homeTeamId;
    }

    toJSON() {
        return {
            ...super.toJSON(),
            returnedBy: playerReducer(this.returnedBy)
        };
    }
}
